import { QueryClient } from '@tanstack/react-query'
import { classifyError } from '../error-kind'

/** Automatic GET retries stay inside the boot machine's own budget of three. */
const MAX_QUERY_RETRIES = 3

const DEFAULT_BACKOFF_CAP_MS = 30_000

/**
 * The server's Retry-After in milliseconds, from a response header in either form (delta seconds or
 * an HTTP date). Undefined when absent or unparseable. A date already past is zero.
 */
export const retryAfterMs = (error: unknown, now: number = Date.now()): number | undefined => {
    const headers = (error as { response?: { headers?: Record<string, unknown> } } | null)?.response?.headers
    const value = headers?.['retry-after']
    if (typeof value !== 'string' && typeof value !== 'number') {
        return undefined
    }

    const text = String(value).trim()
    if (/^\d+$/.test(text)) {
        return Number(text) * 1000
    }

    const at = Date.parse(text)
    return Number.isNaN(at) ? undefined : Math.max(0, at - now)
}

/**
 * The wait before a retry. A server that says when to come back is obeyed, up to the same cap as the
 * default backoff so one header cannot park a boot query for an hour; otherwise the default
 * exponential backoff applies.
 */
export const queryRetryDelay = (failureCount: number, error: unknown): number => {
    const requested = retryAfterMs(error)
    if (requested !== undefined) {
        return Math.min(requested, DEFAULT_BACKOFF_CAP_MS)
    }

    return Math.min(1000 * 2 ** failureCount, DEFAULT_BACKOFF_CAP_MS)
}

/**
 * The app's one query client. A mutation is never retried automatically: the API sends no
 * idempotency key, so a repeated write cannot be told from a new one and the user retries from the
 * failed state. A GET is retried only when it failed transiently, up to MAX_QUERY_RETRIES times;
 * any other kind would fail the same way again.
 */
export const createAppQueryClient = (): QueryClient =>
    new QueryClient({
        defaultOptions: {
            queries: {
                retry: (failureCount, error) =>
                    failureCount < MAX_QUERY_RETRIES && classifyError(error) === 'transient',
                retryDelay: queryRetryDelay,
            },
            mutations: { retry: false },
        },
    })
